/**
 * Railway stations' name boards: at each station or halt about the player
 * (Shortbread's `public_transport` points, `ThreeRenderer.railStationsNear`),
 * a board on two posts beside the nearest track, the name in white on dark
 * blue on both faces, along the track so it is read from a train's window
 * and from the platform. A station mapped as several points of one name has
 * one board. Where no track is near its point, it has none.
 */

import { BoxGeometry, CanvasTexture, Group, Mesh, MeshStandardMaterial, SRGBColorSpace } from 'three';
import type { TrackNetwork, TrackPoint } from './tracks.js';

/** How far from its point a station's track may be, metres; how far out from the track its board stands. */
const TRACK_REACH = 60;
const OFF_TRACK = 4.6;
/** Points of one name this close are one station, metres. */
const SAME = 400;
const BOARD = { width: 4.2, height: 0.75, up: 2.6 };

const post = new BoxGeometry(0.1, BOARD.up + BOARD.height / 2, 0.1);
const postMaterial = new MeshStandardMaterial({ color: 0x5d6268, roughness: 0.6, metalness: 0.4, flatShading: true });
const panel = new BoxGeometry(BOARD.width, BOARD.height, 0.08);
const onTrack: TrackPoint = { x: 0, y: 0, z: 0, dx: 0, dz: 0 };
const best: TrackPoint = { x: 0, y: 0, z: 0, dx: 0, dz: 0 };

interface Board {
  group: Group;
  material: MeshStandardMaterial;
}

export class StationBoards {
  readonly group = new Group();
  private readonly boards = new Map<string, Board>();

  /** Each second or so: the stations about, the railways, and the player at (px, pz). */
  update(stations: ReadonlyArray<{ x: number; z: number; name: string }>, railways: TrackNetwork, px: number, pz: number): void {
    const keep = new Set<string>();
    const placed: Array<{ name: string; x: number; z: number }> = [];
    const byNearness = [...stations].sort((a, b) => Math.hypot(a.x - px, a.z - pz) - Math.hypot(b.x - px, b.z - pz));
    for (const s of byNearness) {
      if (s.name === '' || placed.some(p => p.name === s.name && Math.hypot(p.x - s.x, p.z - s.z) < SAME)) continue;
      // The nearest track to its point, whichever way it runs.
      let found = false, bestD = Infinity;
      for (let k = 0; k < 4; k++) {
        const a = (k * Math.PI) / 4;
        if (!railways.snap(s.x, s.z, Math.cos(a), Math.sin(a), TRACK_REACH, onTrack)) continue;
        const d = Math.hypot(onTrack.x - s.x, onTrack.z - s.z);
        if (d < bestD) { bestD = d; found = true; Object.assign(best, onTrack); }
      }
      if (!found) continue;
      placed.push({ name: s.name, x: s.x, z: s.z });
      keep.add(s.name);
      let board = this.boards.get(s.name);
      if (board === undefined) {
        board = makeBoard(s.name);
        this.boards.set(s.name, board);
        this.group.add(board.group);
      }
      // Beside the track on the station's side, along it.
      const nx = -best.dz, nz = best.dx;
      const side = (s.x - best.x) * nx + (s.z - best.z) * nz >= 0 ? 1 : -1;
      board.group.position.set(best.x + nx * OFF_TRACK * side, best.y, best.z + nz * OFF_TRACK * side);
      board.group.rotation.y = Math.atan2(nx, nz);
    }
    for (const [name, board] of this.boards) {
      if (keep.has(name)) continue;
      this.group.remove(board.group);
      board.material.map?.dispose();
      board.material.dispose();
      this.boards.delete(name);
    }
  }
}

function makeBoard(name: string): Board {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 114;
  const g = canvas.getContext('2d');
  if (g !== null) {
    g.fillStyle = '#24364f';
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.strokeStyle = '#f2f2ee';
    g.lineWidth = 6;
    g.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
    g.fillStyle = '#f7f7f2';
    let size = 70;
    do {
      g.font = `600 ${size}px system-ui, sans-serif`;
      size -= 4;
    } while (g.measureText(name).width > canvas.width - 60 && size > 24);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(name, canvas.width / 2, canvas.height / 2 + 4);
  }
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  const material = new MeshStandardMaterial({ map: texture, roughness: 0.6, flatShading: true });
  const group = new Group();
  const face = new Mesh(panel, material);
  face.position.y = BOARD.up;
  face.castShadow = true;
  group.add(face);
  for (const x of [-BOARD.width / 2 + 0.3, BOARD.width / 2 - 0.3]) {
    const p = new Mesh(post, postMaterial);
    p.position.set(x, (BOARD.up + BOARD.height / 2) / 2, -0.08);
    group.add(p);
  }
  return { group, material };
}
